// Says out loud that a chart is showing an aggregate, and offers the raw series
// where the point count allows (issue #108).
//
// `bucket_size` had been in all three series responses since they were written
// and was never surfaced, so a 27-reading mean was presented in exactly the same
// way as a single reading. That is the half of #108 that is not arithmetic: the
// chart was not wrong to decimate 16,279 rows into 300 points, it was wrong not
// to say it had.
//
// One component, three callers — the same reason there is now one downsampler
// behind the three endpoints.

import { formatSpan } from "../seriesAggregation";

const count = (n) => (Number.isFinite(n) ? n.toLocaleString("en-GB") : "?");

export default function AggregationNotice({
  agg,
  mode,
  onModeChange,
  t = (k) => k,
  canToggle = true,
}) {
  const span = formatSpan(agg.bucketSpanMs);

  // Nothing to toggle unless the series is being aggregated, or the user asked
  // for raw (in which case they need a way back), or the cap refused them.
  const showToggle = canToggle && (mode === "raw" || agg.aggregated || agg.rawCapped);

  const lines = [];
  // Stated first: this is the one case where the chart is not showing what was
  // asked for, and silently degrading is what the issue asked us not to do.
  if (agg.rawCapped) {
    lines.push(t("aggRawCapped", { cap: count(agg.rawMaxPoints), rows: count(agg.rows) }));
  }
  if (agg.aggregated) {
    lines.push(
      span
        ? t("aggNotice", { span, n: count(agg.bucketSize) })
        : t("aggNoticeNoSpan", { n: count(agg.bucketSize) })
    );
    lines.push(t("aggNoticeLatest"));
  } else if (mode === "raw" && Number.isFinite(agg.rows)) {
    lines.push(t("aggRawNotice", { rows: count(agg.rows) }));
  }

  // An unaggregated series with nothing to offer says nothing at all — at 24 h
  // the tilt sensor's 189 readings are already plotted as measured.
  if (!showToggle && lines.length === 0) return null;

  return (
    <div style={styles.row}>
      {showToggle && (
        <div style={styles.toggle}>
          <button
            type="button"
            onClick={() => onModeChange("auto")}
            style={mode === "raw" ? styles.btn : styles.btnActive}
          >
            {t("aggPlotAggregated")}
          </button>
          <button
            type="button"
            onClick={() => onModeChange("raw")}
            style={mode === "raw" ? styles.btnActive : styles.btn}
          >
            {t("aggPlotRaw")}
          </button>
        </div>
      )}
      {lines.length > 0 && (
        <p style={agg.rawCapped ? styles.noteCapped : styles.note}>{lines.join(" ")}</p>
      )}
    </div>
  );
}

const styles = {
  row: { display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10, flexWrap: "wrap" },
  toggle: { display: "flex", gap: 4, flexShrink: 0 },
  btn: { padding: "2px 10px", border: "1px solid #d1d5db", borderRadius: 6, background: "#f9fafb", color: "#6b7280", cursor: "pointer", fontSize: 12 },
  btnActive: { padding: "2px 10px", border: "1px solid #3b82f6", borderRadius: 6, background: "#eff6ff", color: "#1d4ed8", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  note: { margin: 0, fontSize: 11, color: "#6b7280", lineHeight: 1.5, flex: "1 1 320px" },
  noteCapped: { margin: 0, fontSize: 11, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 4, padding: "4px 8px", lineHeight: 1.5, flex: "1 1 320px" },
};
